import { matchPath } from 'react-router-dom';
import { DeviceType } from '@ixicare/resources';
import { t } from '~/i18n';
import routes from './routes';

const HELP_CENTER_URL = 'https://help.ixicare.com';

// The help center ships fewer languages than the dashboard; the rest fall back to its English
// root. Keep in sync with LANGUAGES in help-center/docusaurus.config.ts.
const HELP_CENTER_LANGUAGES = new Set([
  'en',
  'nl',
  'fr',
  'de',
  'it',
  'es',
  'pt',
  'da',
  'no',
  'sv',
  'fi',
  'is',
  'tr',
  'el',
  'bg',
]);

interface HelpArticle {
  /** Docs path under the help-center root, e.g. `handle-an-alarm/groups`. */
  path: string;
  /** What the article is about, named the way the dashboard names it. */
  topic: () => string;
}

// First match wins, so sub-routes that need a different article go before their parent's `/*`.
const ARTICLES: [pattern: string, article: HelpArticle][] = [
  [routes.dashboard, { path: 'dashboard/overview', topic: () => t('menu.dashboard.dashboard') }],
  [`${routes.alerts}/*`, { path: 'handle-an-alarm/handling-alarms', topic: () => t('menu.dashboard.alerts') }],
  [`${routes.residents}/*`, { path: 'setup-a-device/alarm-sender', topic: () => t('menu.dashboard.residents') }],
  [
    `${routes.healthcareProviders}/*`,
    { path: 'handle-an-alarm/alarm-receiver', topic: () => t('menu.dashboard.healthcareProviders') },
  ],
  [routes.hcpGroups, { path: 'handle-an-alarm/groups', topic: () => t('menu.dashboard.hcpGroups') }],
  [routes.configuration, { path: 'dashboard/settings', topic: () => t('menu.dashboard.configuration') }],
  [routes.sites, { path: 'setup-a-site/site', topic: () => t('menu.dashboard.settingsSites') }],
  [`${routes.sites}/create`, { path: 'setup-a-site/site', topic: () => t('menu.dashboard.settingsSites') }],
  [`${routes.sites}/*`, { path: 'setup-a-site/floor-plans', topic: () => t('global.floorPlans') }],
  [routes.deviceHealth, { path: 'setup-a-device/device-health', topic: () => t('menu.dashboard.deviceHealth') }],
  [routes.deviceOverview, { path: 'setup-a-device/device', topic: () => t('alerts.status.devices') }],
  [
    `${routes.deviceOverview}/*`,
    { path: 'setup-a-device/device-configuration', topic: () => t('menu.dashboard.deviceConfiguration') },
  ],
  [routes.statistics, { path: 'dashboard/statistics', topic: () => t('menu.dashboard.statistics') }],
];

// Device types with their own pages. The device overview keeps its tab in the URL hash, and the
// configuration pages load one form per device type, so neither shows in the path.
const TAB_ARTICLES: Record<string, HelpArticle> = {
  eviews: { path: 'devices/eview', topic: () => t('device.types.eview') },
  twigs: { path: 'devices/twig', topic: () => t('device.types.twig') },
};
const CONFIG_ARTICLES: Partial<Record<DeviceType, HelpArticle>> = {
  [DeviceType.IXI_PRO]: { path: 'setup-a-device/pro-settings', topic: () => t('device.types.ixi_pro') },
  [DeviceType.EVIEW]: { path: 'setup-a-device/eview-settings', topic: () => t('device.types.eview') },
  [DeviceType.TWIG]: { path: 'setup-a-device/twig-settings', topic: () => t('device.types.twig') },
};

const findArticle = (pathname: string, hash: string, deviceType?: DeviceType) => {
  if (matchPath(routes.deviceOverview, pathname)) {
    const tabArticle = TAB_ARTICLES[hash.replace('#', '')];
    if (tabArticle) return tabArticle;
  } else if (deviceType && matchPath(`${routes.deviceOverview}/*`, pathname)) {
    const configArticle = CONFIG_ARTICLES[deviceType];
    if (configArticle) return configArticle;
  }
  return ARTICLES.find(([pattern]) => matchPath(pattern, pathname))?.[1];
};

const helpCenterRoot = (language: string) =>
  HELP_CENTER_LANGUAGES.has(language) && language !== 'en' ? `${HELP_CENTER_URL}/${language}` : HELP_CENTER_URL;

/** A help-center page in the user's language, e.g. `devices/eview#connect`. */
export const helpCenterUrl = (path: string, language: string) => `${helpCenterRoot(language)}/${path}`;

/**
 * The help-center link and tooltip for a dashboard page; the help-center home when no article covers it.
 * `deviceType` is the type whose configuration form is loaded.
 */
export const resolveHelp = (
  pathname: string,
  hash: string,
  language: string,
  deviceType?: DeviceType
): { href: string; label: string } => {
  const article = findArticle(pathname, hash, deviceType);
  if (!article) {
    return { href: helpCenterUrl('', language), label: t('global.help') };
  }
  return { href: helpCenterUrl(article.path, language), label: t('global.helpTopic', { topic: article.topic() }) };
};
